import { apiGatewayClientWithCredentials as getApiGatewayClient } from "services/api";
import { store } from "./state";
import { REST } from "utils/constants";

const getAccountsWithFilter = async (filter) => {
  const apiGatewayClient = await getApiGatewayClient();
  const response = await apiGatewayClient.get(
    "/admin/accounts",
    undefined,
    undefined,
    { queryParams: { filter } }
  );
  return response.data.accounts;
};

const url = ([first, ...rest], ...parts) =>
  first + parts.map((p, i) => encodeURIComponent(p) + rest[i]).join("");

const fetch = (method, target, params = {}) =>
  getApiGatewayClient()
    .then((apiGatewayClient) =>
      apiGatewayClient[method](target, {}, params, {})
    )
    .then((response) => response)
    .catch((error) => {
      throw error && (error.data || error);
    });

export const fetchRegisteredAccounts = () =>
  getAccountsWithFilter("registered");
export const fetchAdminAccounts = () => getAccountsWithFilter("admin");
export const fetchOnlyRestAccounts = () =>
  getAccountsWithFilter("rest-apis-users");
export const fetchOnlyWsAccounts = () =>
  getAccountsWithFilter("websocket-apis-users");
export const fetchIkanoUserAccounts = () =>
  getAccountsWithFilter("ikano-users");
export const fetchNonIkanoUserAccounts = () =>
  getAccountsWithFilter("non-ikano-users");
export const fetchDeveloperToolsAccounts = () =>
  getAccountsWithFilter("developer-tools-users");
export const fetchPendingRequestAccounts = () =>
  getAccountsWithFilter("request");
export const fetchPendingInviteAccounts = () =>
  getAccountsWithFilter("pendingInvite");

export const deleteAccountsByUserId = async (accounts) => {
  await accounts.forEach(async (account) => {
    const response = await fetch(
      "delete",
      url`/admin/accounts/${account.UserId}`
    );

    if (response.status === 200) {
      await fetch("post", url`/admin/notify/deletion`, {
        type: "UserDeletion",
        email: account.EmailAddress,
      });
    }
  });
};

const sendAccessRequestNotification = async (email, page) => {
  await fetch("post", "/admin/notify/accessRequest", {
    type: "AccessRequest",
    user: email,
    page: page,
  });
};

export const sendAccessRequestToUserGroup = async (UserId, email, apiType) => {
  const response = await fetch("post", "/admin/accounts/sendRequest", {
    UserId,
    apiType,
  });
  if (response.status !== 200) {
    throw new Error("Something went wrong");
  }

  try {
    //Sending the email notification to the admins
    const page = apiType === REST ? "REST APIs" : "WebSocket APIs";
    await sendAccessRequestNotification(email, page);
  } finally {
    return;
  }
};

// TODO: verify if this is even correct - I suspect it should've posted to
// `/admin/accounts/:userId/denyRequest` instead.
export const deleteInviteByUserId = (userId) =>
  fetch("delete", url`/admin/accounts/${userId}`);
export const createInviteByEmail = (email) =>
  fetch("post", "/admin/accounts", { targetEmailAddress: email });
export const resendInviteByEmail = (email) =>
  fetch("put", "/admin/accounts/resendInvite", { targetEmailAddress: email });

export const promoteAccountByUserId = async (accounts) => {
  await accounts.forEach(async (account) => {
    const response = await fetch(
      "put",
      url`/admin/accounts/${account.UserId}/promoteToAdmin`
    );

    if (response.status === 200) {
      await fetch("post", url`/admin/notify/adminPromotion`, {
        type: "AdminPromotion",
        email: account.EmailAddress,
      });
    }
  });
};

export const approveAccountRequestsByUserId = async (accounts) => {
  accounts.forEach(async ({ UserId, RequestedPage, EmailAddress }) => {
    const apiType = RequestedPage?.split(' ')[0].toLowerCase()
    const response =
      await fetch("put", url`/admin/accounts/approveRequest`, {
        UserId,
        apiType,
        promoterUserId: store.user.UserId
      })

    if (response.status === 200) {
      await fetch("post", url`/admin/notify/approveRequest`, {
        type: "AccessApproval",
        email: EmailAddress,
        page: RequestedPage,
      });
    }
  })
}

export const denyAccountRequestsByUserId = async (accounts) => {
  accounts.forEach(async ({ UserId, RequestedPage, EmailAddress }) => {
    const apiType = RequestedPage?.split(' ')[0].toLowerCase()
    const response =
      await fetch("put", url`/admin/accounts/denyRequest`, {
        UserId,
        apiType,
        adminUserId: store.user.UserId
      })

    if (response.status === 200) {
      await fetch("post", url`/admin/notify/denyRequest`, {
        type: "AccessDenial",
        email: EmailAddress,
        page: RequestedPage,
      });
    }
  })
}

//user groups accounts
export const promoteAccountByUserIdToUserGroups = async (
  accounts,
  userGroups
) => {
  const userGroupParameterName = {
    "Rest API group": "RestAPIOnlyUserName",
    "Websocket API group": "WsAPIOnlyUserName",
    "Ikano user group": "IkanoUserGroupName",
    "Non Ikano user group": "NonIkanoUserGroupName",
    "Developer Tools Group": "DeveloperToolsUserGroupName",
  };

  // get the key value of group names
  const getKeyForValue = (object, targetValue) => {
    for (const key in object) {
      if (object[key] === targetValue) {
        return key;
      }
    }
    return null; // Return null if the value is not found
  };

  for (const userGroup of userGroups) {
    accounts.forEach(async (account) => {
      const userGroupName = userGroupParameterName[userGroup];

      if (userGroupName) {
        const response = await fetch(
          "put",
          url`/admin/accounts/${account.UserId}/${userGroupName}`
        );
        if (response.status === 200) {
          const groupName = getKeyForValue(
            userGroupParameterName,
            userGroupName
          );
          await fetch("post", url`/admin/notify/userPromotion`, {
            type: "UserPromotion",
            email: account.EmailAddress,
            promotedResource: groupName || userGroup,
          });
        }
      }
    });
  }
};

export const deleteAccountsFromUserGroup = async (accounts, groupName) => {
  const userGroups = {
    RestAPIOnlyUserName: "REST APIs",
    WsAPIOnlyUserName: "WebSocket APIs",
    IkanoUserGroupName: "Ikano Users",
    NonIkanoUserGroupName: "Non Ikano Users",
    DeveloperToolsUserGroupName: "Developer Tools",
  };

  await accounts.forEach(async (account) => {
    const response = await fetch(
      "delete",
      url`/admin/accounts/${account.UserId}/${groupName}/removeUserFromUserGroup`
    );

    if (response.status === 200) {
      await fetch("post", url`/admin/notify/revokedAccess`, {
        type: "AccessRevoked",
        email: account.EmailAddress,
        revokedResource: userGroups[groupName],
      });
    }
  });
};
